/// <reference types="resize-observer-browser" />
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  ButtonBase,
  Checkbox,
  IconButton,
  Slider,
  Typography
} from '@material-ui/core';
import {
  compact,
  groupBy,
  identity,
  orderBy,
  padStart,
  sortBy,
  times,
  uniq
} from 'lodash';
import { LucideIcon as Icon, Minus, Plus } from 'lucide-react';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHover } from '../../hooks/useHover';
import {
  RelativeDragHandlers,
  useRelativeDrag
} from '../../hooks/useRelativeDrag';
import { useResizeObserver } from '../../hooks/useResizeObserver';
import { FlexContainer } from '../../layout/Flex';
import { debuggedShallowEqual } from '../../services/debug';
import { useTheme } from '../../themes';

const ENABLE_SCROLL_WHEEL_ZOOM = false;

const Z_INDICES = {
  BG: 0,
  RULER: 1,
  CENTER_RULER: 2,
  ITEM: 3,
  RESIZE_HANDLE: 4,
  ANNOTATION: 1
};

export type TimelineItem = {
  key: string;
  start: number;
  end: number;
  body: React.ReactNode;
  resizable?: boolean;
  movable?: boolean;
  data?: any;
  parents?: string[];
  children?: string[];
};

export type TimelineAnnotation = {
  key: string;
  start: number;
  end: number;
  body: React.ReactNode;
  center?: boolean;
};

export type TimelineRuler = {
  position: number;
  label: React.ReactNode;
  alignLabel?: 'left' | 'center' | 'right'; // defaults to 'center'
  colorMode?: 'light' | 'dark'; // defaults to 'dark'
};

export type TimelineSecondaryHeaderElement = {
  key: string;
  start: number;
  end: number | null;
  body: React.ReactNode;
  lane: number;
  movable?: boolean;
  data?: any;
};

type Props = {
  items: TimelineItem[];
  renderOuterUnit?: (i: number, gridOffset: number) => React.ReactNode;
  innerUnitSpan: number;
  outerUnitSpan?: number;
  minUnits?: number;
  maxUnits?: number;
  minRows?: number;
  minRowHeight?: number;
  grid?: boolean;
  header?: boolean;
  secondaryHeaderElements?: TimelineSecondaryHeaderElement[];
  create?: {
    renderPlaceholder: () => React.ReactNode;
    onCreate: (
      d: { start: number; end: number },
      trigger: 'drag' | 'click'
    ) => void;
    defaultDuration?: number;
  };
  createSecondaryHeaderElement?: {
    // renderPlaceholder: () => React.ReactNode; // not used - we just do clicking atm
    onCreate: (
      d: { start: number; end: number | null },
      trigger: 'drag' | 'click'
    ) => void;
    defaultDuration?: number | null;
  };
  annotations?: TimelineAnnotation[];
  alwaysDrawAnnotationLane?: boolean;
  onMove?: (
    key: string,
    positions: {
      next: { start: number; end: number };
      prev: { start: number; end: number };
      diff: { start: number; end: number };
    },
    data: any
  ) => void;
  onSecondaryHeaderMove?: (
    key: string,
    start: number,
    end: number | null,
    data: any
  ) => void;
  scale?: number;
  onScaleChangeRequest?: (deltaY: number) => void;
  scrollIntoView?: number;
  emptyOverlay?: React.ReactNode;
  rulers?: TimelineRuler[];
};

const Grid = styled('div')<{
  columns: number;
  rows: number;
  gridRows: number;
  rulerRows: number;
  headerRows: number;
  secondaryHeaderRows: number;
  minRowHeight?: number;
  minColWidth?: number;
}>`
  position: relative;
  display: grid;
  flex: 1;
  grid-template-columns: repeat(
    ${(p) => p.columns},
    ${(p) => (p.minColWidth ? `minmax(${p.minColWidth}px, 1fr)` : '1fr')}
  );
  grid-template-rows:
    ${(p) => (p.rulerRows === 1 ? `${p.theme.spacing(4)}px` : null)}
    ${(p) => (p.headerRows === 1 ? 'auto' : null)}
    ${(p) => (p.secondaryHeaderRows === 1 ? 'auto' : null)}
    repeat(${(p) => p.gridRows}, auto)
    1fr;
  grid-row-gap: ${(p) => p.theme.spacing(0.5)}px;
  overflow: overlay;
  padding-bottom: ${(p) => p.theme.spacing(1)}px;
  z-index: 0;
  &::-webkit-scrollbar {
    background-color: ${(p) => p.theme.custom.colors.gray.white};
    width: ${(p) => p.theme.spacing()}px;
    height: ${(p) => p.theme.spacing()}px;
  }
  &:hover {
    &::-webkit-scrollbar-thumb {
      background-color: ${(p) => p.theme.custom.colors.gray.w300};
      border-radius: ${(p) => p.theme.custom.border.radius()}px;
      &:hover {
        background-color: ${(p) => p.theme.custom.colors.gray.w400};
      }
    }
  }
`;

const GRID_ITEM_SIDE_MARGIN_MULTIPLIER = 0.5;

const GridItem = styled('div')<{
  start: number;
  end: number;
  order: number;
}>`
  position: relative;
  grid-column: ${(p) => `${p.start + 1} / span ${p.end - p.start}`};
  grid-row: ${(p) => `${p.order + 1} / ${p.order + 2}`};
  margin: 0 ${(p) => p.theme.spacing(GRID_ITEM_SIDE_MARGIN_MULTIPLIER)}px;
  z-index: ${Z_INDICES.ITEM};
`;

const Header = styled('div')<{ start: number; span: number; offset: number }>`
  grid-column: ${(p) => p.start * p.span + 1} / span ${(p) => p.span};
  grid-row: ${(p) => 1 + p.offset} / ${(p) => 2 + p.offset};
  display: flex;
  justify-content: center;
  align-items: flex-end;
  text-align: center;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  font-size: ${(p) => p.theme.custom.fontSize.xs}px;
  padding-bottom: ${(p) => p.theme.spacing(0.5)}px;
`;

const SecondaryHeaderElementsBackground = styled('div')<{
  cols: number;
  row: number;
  rows: number;
}>((p) => ({
  gridColumn: `1 / span ${p.cols}`,
  gridRow: `${p.row + 1} / ${p.row + 1 + p.rows}`,
  background: p.theme.custom.colors.gray.w300,
  zIndex: Z_INDICES.BG,
  opacity: 0.4,
  marginBottom: p.theme.spacing(1) // works in tandem with the marginBottom of SecondaryHeaderElement
}));

const SecondaryHeaderElementContainer = styled('div')<{
  start: number;
  end: number;
  row: number;
  isFirstLane: boolean;
  isLastLane: boolean;
}>((p) => ({
  position: 'relative',
  gridColumn: `${p.start + 1} / span ${p.end - p.start}`,
  margin: `${p.theme.spacing(0)}px ${p.theme.spacing(
    GRID_ITEM_SIDE_MARGIN_MULTIPLIER
  )}px`,
  marginBottom: p.theme.spacing(p.isLastLane ? 2 : 0.5), // more bottom margin, so that we can distance the whole row better - it's hacky
  marginTop: p.theme.spacing(p.isFirstLane ? 1 : 0),
  gridRow: p.row + 1,
  display: 'flex',
  alignItems: 'center',
  zIndex: Z_INDICES.ITEM
}));

// required so that the timeline cannot overflow and is forced to scroll
const GridContainer = styled('div')`
  display: grid;
  position: relative;
  flex: 1;
`;

const SecondaryHeaderElement = React.memo(
  ({
    e,
    innerUnitSpan,
    gridWidth,
    minColWidth,
    row,
    dragHandlers,
    dragRef,
    drag,
    setDrag,
    onMove,
    onOuterClick,

    isFirstLane,
    isLastLane,
    gridOffset
  }: {
    innerUnitSpan: number;
    gridWidth: number;
    minColWidth: number;

    row: number;
    e: TimelineSecondaryHeaderElement;
    dragHandlers: RelativeDragHandlers;
    dragRef: React.RefObject<HTMLDivElement>;
    drag: DragState<TimelineSecondaryHeaderElement>;
    setDrag: React.Dispatch<
      React.SetStateAction<DragState<TimelineSecondaryHeaderElement>>
    >;
    onMove?: (
      key: string,
      start: number,
      end: number | null,
      data: any
    ) => void;
    onOuterClick: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    isFirstLane: boolean;
    isLastLane: boolean;
    gridOffset: number;
  }) => {
    const getUnitWidth = (gridEl: HTMLDivElement) =>
      Math.max(minColWidth, gridEl.getBoundingClientRect().width / gridWidth);

    const ref = useRef(null);
    return (
      <SecondaryHeaderElementContainer
        key={e.key}
        start={Math.floor((e.start - gridOffset) / innerUnitSpan)}
        end={
          e.end ? Math.ceil((e.end - gridOffset) / innerUnitSpan) : gridWidth
        }
        isFirstLane={isFirstLane}
        isLastLane={isLastLane}
        row={row}
        ref={ref}
        draggable={!!e.movable && !!onMove}
        onClick={(ev) => {
          if (ev.target === ref.current) {
            onOuterClick(ev);
          }
        }}
        onDragStart={(ev) => {
          if (e.movable && onMove) {
            setDrag({
              key: e.key,
              item: e,
              mode: 'move-all',
              currentOrder: 0
            });
            dragHandlers.onDragStart(ev);
          }
          hideDragPreview(ev);
        }}
        onDrag={(ev) => {
          if (
            onMove &&
            e.movable &&
            drag &&
            drag.mode === 'move-all' &&
            drag.item.key === e.key
          ) {
            dragHandlers.onDrag(ev, (nextPos) => {
              if (!dragRef.current) {
                return;
              }
              const unitWidth = getUnitWidth(dragRef.current);
              const steps =
                nextPos.x < 0
                  ? Math.floor(nextPos.x / unitWidth)
                  : Math.ceil(nextPos.x / unitWidth);

              const nextStart = drag.item.start + steps * innerUnitSpan;
              const nextEnd =
                drag.item.end === null
                  ? null
                  : drag.item.end + steps * innerUnitSpan;

              if (nextStart < 0) {
                return;
              }

              if (e.start !== nextStart || e.end !== nextEnd) {
                onMove(drag.item.key, nextStart, nextEnd, drag.item.data);
              }
            });
          }
        }}
        onDragEnd={(ev) => {
          ev.stopPropagation();
          setDrag(null);
          dragHandlers.onDragEnd(ev);
        }}
      >
        {e.body}
      </SecondaryHeaderElementContainer>
    );
  }
);

const areOverlapping = (a: [number, number], b: [number, number]) => {
  return !(
    (b[0] >= a[0] && b[0] >= a[1] && b[1] >= a[0] && b[1] >= a[1]) ||
    (b[0] <= a[0] && b[0] <= a[1] && b[1] <= a[0] && b[1] <= a[1])
  );
};

const getOrder = (
  rows: TimelineItem[][],
  d: TimelineItem,
  minOrder: number | null
) => {
  let order = minOrder || 0;
  for (const row of rows.slice(order)) {
    if (
      !!row.find((el) => {
        return areOverlapping([el.start, el.end], [d.start, d.end]);
      })
    ) {
      order++;
    } else {
      return order;
    }
  }
  return order;
};

type TimelineItemWithDimensions = {
  xEnd: number;
  xStart: number;
  item: TimelineItem;
};

const getOrder2 = (
  rows: TimelineItemWithDimensions[][],
  d: { xStart: number; xEnd: number; ySpan: number; items: TimelineItem[] }
) => {
  let order = 0;
  while (true) {
    const rs = rows.slice(order, order + d.ySpan);
    const overlappingI = rs.findIndex((row) => {
      const overlappingEl = row.find((el) => {
        return areOverlapping([el.item.start, el.xEnd], [d.xStart, d.xEnd]);
      });
      return !!overlappingEl;
    });
    if (overlappingI === -1) {
      return order;
    } else {
      order += overlappingI + 1;
    }
  }
};

// provide rendered components like a head from the outside
// That's where translations can be done then too

const CoordinateItemOuter = styled('div')<{
  start: number;
  span: number;
  rows: number;
  offset: number;
}>`
  grid-column: ${(p) => p.start * p.span + 1} / span ${(p) => p.span};
  grid-row: ${(p) => p.offset + 1} / span ${(p) => p.rows};
  :first-of-type {
    border-left: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  }
  border-right: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  pointer-events: none;
`;

const CoordinateItemInner = styled('div')<{
  start: number;
  row: number;
  offset: number;
}>`
  grid-column: ${(p) => p.start} / span 1;
  grid-row: ${(p) => p.row + p.offset} / span 1;
  border-right: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  margin-top: ${(p) => p.theme.spacing()}px;
  height: ${(p) => p.theme.spacing(1)}px;
  align-self: self-end;
`;

const Coordinates = React.memo(
  ({
    rows,
    span,
    ticks,
    offset
  }: {
    rows: number;
    span: number;
    ticks: number;
    offset: number;
  }) => {
    return (
      <>
        {new Array(ticks).fill(null).map((_, i) => (
          <CoordinateItemOuter
            key={i}
            rows={rows}
            span={span}
            start={i}
            offset={offset}
          />
        ))}
        {new Array(ticks * span)
          .fill(null)
          .map((_, i) =>
            i % span === 0 ? null : (
              <CoordinateItemInner
                key={i}
                row={rows}
                start={i}
                offset={offset}
              />
            )
          )}
      </>
    );
  }
);

const ResizerHandle = styled('div')<{
  position: 'left' | 'right';
  width: number;
}>((p) => {
  const common: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    width: p.width,
    height: '100%',
    zIndex: Z_INDICES.RESIZE_HANDLE
  };
  if (p.position === 'left') {
    return {
      ...common,
      left: 0,
      cursor: 'w-resize !important'
    };
  }

  return {
    ...common,
    right: 0,
    cursor: 'e-resize !important'
  };
});

const hideDragPreview = (ev: React.DragEvent) => {
  const img = new Image();
  img.src =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
  ev.dataTransfer.setDragImage(img, 0, 0);
};

const RESIZER_UNIT = 10;

const getNextResizerWidth = (elWidth: number) => {
  if (elWidth > RESIZER_UNIT * 3) {
    return RESIZER_UNIT;
  }
  if (elWidth > RESIZER_UNIT * 2) {
    return RESIZER_UNIT / 2;
  }
  return 0;
};

const CenterAnnotation = styled('div')((p) => ({
  // this actually should be done through absolute positioning i think - so that verticality is correct and overlaying if need be
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const AnnotationGridItem = styled(GridItem)(() => ({
  zIndex: Z_INDICES.ANNOTATION
}));

const Annotation = React.memo(
  ({
    d,
    row,
    innerUnitSpan,
    minColWidth,
    gridWidth,
    gridOffset
  }: {
    d: TimelineAnnotation;

    row: number;
    innerUnitSpan: number;

    minColWidth: number;
    gridWidth: number;

    gridOffset: number;
  }) => {
    return (
      <AnnotationGridItem
        start={Math.floor((d.start - gridOffset) / innerUnitSpan)}
        end={Math.ceil((d.end - gridOffset) / innerUnitSpan)}
        order={row}
      >
        {d.center ? <CenterAnnotation>{d.body}</CenterAnnotation> : d.body}
      </AnnotationGridItem>
    );
  }
);

const Item = React.memo(
  ({
    r,
    innerUnitSpan,
    headerRows,
    onMove,
    order,
    dragHandlers,
    drag,
    setDrag,
    dragRef,
    minColWidth,
    gridWidth,
    gridOffset
  }: {
    r: TimelineItem;
    innerUnitSpan: number;
    headerRows: number;
    onMove?: (
      key: string,
      positions: {
        next: { start: number; end: number };
        prev: { start: number; end: number };
        diff: { start: number; end: number };
      },
      data: any
    ) => void;

    order: number;

    dragHandlers: RelativeDragHandlers;
    dragRef: React.RefObject<HTMLDivElement>;
    drag: DragState<TimelineItem>;
    setDrag: React.Dispatch<React.SetStateAction<DragState<TimelineItem>>>;

    minColWidth: number;
    gridWidth: number;
    gridOffset: number;
  }) => {
    const [resizeHandlerWidth, setResizeHandlerWidth] = useState(RESIZER_UNIT);
    const { resizable } = r;
    const resizeObserverFns = useMemo(() => {
      return {
        onResize: (entry: ResizeObserverEntry) => {
          const { width } = entry.contentRect;
          setResizeHandlerWidth(getNextResizerWidth(width));
        },
        onInit: (el: Element) => {
          setResizeHandlerWidth(
            getNextResizerWidth(el.getBoundingClientRect().width)
          );
        },
        shouldObserve: () => !!resizable
      };
    }, [resizable]);

    const ref = useResizeObserver(
      resizeObserverFns.onResize,
      resizeObserverFns.onInit,
      resizeObserverFns.shouldObserve
    );

    const getUnitWidth = (gridEl: HTMLDivElement) =>
      Math.max(minColWidth, gridEl.getBoundingClientRect().width / gridWidth);

    return (
      <GridItem
        key={r.key}
        ref={ref}
        start={Math.floor((r.start - gridOffset) / innerUnitSpan)}
        end={Math.ceil((r.end - gridOffset) / innerUnitSpan)}
        order={order + headerRows}
        draggable={!!onMove}
        onDragStart={(ev) => {
          if (r.movable && onMove) {
            setDrag({
              key: r.key,
              item: r,
              mode: 'move-all',
              currentOrder: order
            });
            dragHandlers.onDragStart(ev);
          }
          hideDragPreview(ev);
        }}
        onDrag={(ev) => {
          if (
            onMove &&
            r.movable &&
            drag &&
            drag.mode === 'move-all' &&
            drag.item.key === r.key
          ) {
            dragHandlers.onDrag(ev, (nextPos) => {
              if (!dragRef.current) {
                return;
              }
              const unitWidth = getUnitWidth(dragRef.current);
              const steps =
                nextPos.x < 0
                  ? Math.floor(nextPos.x / unitWidth)
                  : Math.ceil(nextPos.x / unitWidth);

              const nextStart = drag.item.start + steps * innerUnitSpan;
              const nextEnd = drag.item.end + steps * innerUnitSpan;

              if (nextStart < 0) {
                return;
              }

              if (r.start !== nextStart || r.end !== nextEnd) {
                onMove(
                  drag.item.key,
                  {
                    next: { start: nextStart, end: nextEnd },
                    prev: { start: r.start, end: r.end },
                    diff: { start: nextStart - r.start, end: nextEnd - r.end }
                  },
                  drag.item.data
                );
              }
            });
          }
        }}
        onDragEnd={(ev) => {
          ev.stopPropagation();
          setDrag(null);
          dragHandlers.onDragEnd(ev);
        }}
      >
        {r.resizable && onMove && (
          <>
            <ResizerHandle
              key={`${r.key}-resizer-left`}
              draggable={true}
              position="left"
              width={resizeHandlerWidth}
              onDragStart={(ev) => {
                ev.stopPropagation();
                setDrag({
                  key: r.key,
                  item: r,
                  mode: 'move-start',
                  currentOrder: order
                });
              }}
              onDragEnd={(ev) => {
                ev.stopPropagation();
                setDrag(null);
              }}
            />
            <ResizerHandle
              key={`${r.key}-resizer-right`}
              draggable={true}
              position="right"
              width={resizeHandlerWidth}
              onDragStart={(ev) => {
                ev.stopPropagation();
                setDrag({
                  key: r.key,
                  item: r,
                  mode: 'move-end',
                  currentOrder: order
                });
              }}
              onDragEnd={(ev) => {
                ev.stopPropagation();
                setDrag(null);
              }}
            />
          </>
        )}
        {r.body}
      </GridItem>
    );
  },
  debuggedShallowEqual((p) => `TimelineItem ${p.r.key}`, { disable: true })
);

type DragState<T> = null | {
  key: string;
  item: T;
  currentOrder: number | null;
  mode: 'move-start' | 'move-end' | 'move-all';
};

const Headers = React.memo(
  ({
    outerTicks,
    outerUnitMultiplier,
    renderOuterUnit,
    offset = 0
  }: {
    outerTicks: number;
    outerUnitMultiplier: number;
    renderOuterUnit: (i: number) => React.ReactNode;
    offset?: number;
  }) => {
    return (
      <>
        {new Array(outerTicks).fill(null).map((_, i) => (
          <Header key={i} start={i} span={outerUnitMultiplier} offset={offset}>
            {renderOuterUnit(i)}
          </Header>
        ))}
      </>
    );
  }
);

const OverlayContainer = styled('div')<{
  startRow: number;
  rows: number;
  startColumn: number;
  columns: number;
}>((p) => ({
  position: 'relative',
  gridColumn: `${p.startColumn} / span ${p.columns}`,
  gridRow: `${p.startRow} / span ${p.rows}`,
  zIndex: 0
}));

const MainDragContainer = styled('div')<{
  startRow: number;
  rows: number;
  columns: number;
}>((p) => ({
  position: 'relative',
  gridColumn: `1 / span ${p.columns}`,
  gridRow: `${p.startRow} / span ${p.rows}`,
  zIndex: 0
}));

const RulerContainer = styled('div')<{
  startRow: number;
  rows: number;
  pos: number;
  color: string;
  zIndex: number;
}>((p) => ({
  gridRow: `${p.startRow + 1} / span ${p.rows}`,
  gridColumn: p.pos + 1,
  borderLeft: `3px solid ${p.color}`,
  marginLeft: '-1px',
  zIndex: p.zIndex
}));

const RulerLabel = styled('div')<{
  bgColor: string;
  transform: string;
  borderRadius: string;
}>((p) => {
  return {
    position: 'absolute',
    width: 'max-content',
    color: p.theme.palette.background.paper,
    backgroundColor: p.bgColor,
    padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(1)}px`,
    fontSize: p.theme.custom.fontSize.s,
    transform: p.transform,
    borderRadius: p.borderRadius
  };
});

const Rulers = ({
  ds,
  startRow,
  rows,
  innerUnitSpan,
  gridOffset
}: {
  ds: TimelineRuler[];
  startRow: number;
  rows: number;
  innerUnitSpan: number;
  gridOffset: number;
}) => {
  const theme = useTheme();
  const borderRadius = theme.custom.border.radius();

  const renderRuler = (d: TimelineRuler, i: number) => {
    return (
      <RulerContainer
        startRow={startRow}
        rows={rows}
        pos={Math.round((d.position - gridOffset) / innerUnitSpan)}
        key={i}
        color={
          d.colorMode === 'light'
            ? theme.custom.colors.gray.w500
            : theme.palette.text.primary
        }
        zIndex={
          d.alignLabel !== 'left' && d.alignLabel !== 'right'
            ? Z_INDICES.CENTER_RULER
            : Z_INDICES.RULER
        }
      >
        <RulerLabel
          bgColor={
            d.colorMode === 'light'
              ? theme.custom.colors.gray.w500
              : theme.palette.text.primary
          }
          transform={
            d.alignLabel === 'left'
              ? 'translate(-101%, 0%)'
              : d.alignLabel === 'right'
              ? 'translate(-2%, 0)'
              : 'translate(-50%, 0)'
          }
          borderRadius={
            d.alignLabel === 'left'
              ? `${borderRadius}px 0px 0px ${borderRadius}px`
              : d.alignLabel === 'right'
              ? `0px ${borderRadius}px ${borderRadius}px 0px`
              : `${borderRadius}px`
          }
        >
          {d.label}
        </RulerLabel>
      </RulerContainer>
    );
  };

  return <>{ds.map((d, i) => renderRuler(d, i))}</>;
};

export const Timeline = ({
  items,
  renderOuterUnit = identity,
  innerUnitSpan,
  outerUnitSpan = innerUnitSpan,
  minUnits = 0,
  maxUnits = Infinity,
  minRows = 0,
  minRowHeight,
  grid = true,
  header = true,
  secondaryHeaderElements,
  create,
  createSecondaryHeaderElement,
  onMove,
  onSecondaryHeaderMove,
  scale = 1,
  onScaleChangeRequest,
  annotations = [],
  alwaysDrawAnnotationLane,
  scrollIntoView,
  emptyOverlay,
  rulers
}: Props) => {
  const [layout] = useState<'GROUPED' | 'COMPACT'>('COMPACT');
  const theme = useTheme();
  const minColWidth = theme.spacing(1.5) * scale;
  const [mainDrag, setMainDrag] = useState<DragState<TimelineItem>>(null);
  const [secondaryDrag, setSecondaryDrag] = useState<
    DragState<TimelineSecondaryHeaderElement>
  >(null);
  const [mainPlaceholder, setMainPlaceholder] = useState<TimelineItem | null>(
    null
  );
  const gridRef = useRef<HTMLDivElement>(null);
  const mainDragRef = useRef<HTMLDivElement>(null);
  const secondaryDragRef = useRef<HTMLDivElement>(null);

  // If any item is rendered with a negative start/position value, we need to shift the whole grid around.
  // Negative grid position values render items from the end - which is not what we want. All items need be aligned
  // towards an offset 0 value.
  const gridOffset = useMemo(() => {
    let minStart = 0;
    items.forEach((item) => {
      minStart = Math.min(minStart, item.start);
    });
    rulers?.forEach((ruler) => {
      minStart = Math.min(minStart, ruler.position);
    });
    annotations.forEach((annotation) => {
      minStart = Math.min(minStart, annotation.start);
    });
    secondaryHeaderElements?.forEach((el) => {
      minStart = Math.min(minStart, el.start);
    });
    return minStart;
  }, [items, rulers, annotations, secondaryHeaderElements]);

  // A timeline item without a valid `end` (e.g. a point event like a calendar
  // invite, or an item whose timing hasn't resolved yet) must not poison the
  // grid sizing: `Math.max(…, NaN, …)` is NaN, which flows into `gridWidth` and
  // `outerTicks`, making `new Array(ticks)` throw "Invalid array length". Fall
  // back to the item's `start` when `end` is missing/non-finite (mirrors the
  // secondaryHeaderElements handling below) and coerce any non-finite unit to 0.
  const toEndUnit = (raw: number) => {
    const v = Math.ceil((raw - gridOffset) / innerUnitSpan);
    return Number.isFinite(v) ? v : 0;
  };
  const endOrStart = (end: number | null | undefined, start: number) =>
    end != null && Number.isFinite(end) ? end : start;
  const trueEnd = Math.max(
    0,
    ...items.map((i) => toEndUnit(endOrStart(i.end, i.start))),
    ...(secondaryHeaderElements || []).map((i) =>
      toEndUnit(endOrStart(i.end, i.start))
    ),
    ...(rulers || []).map((r) => {
      const v = Math.ceil(r.position - gridOffset) / innerUnitSpan;
      return Number.isFinite(v) ? v : 0;
    })
  );

  const outerUnitMultiplier = Math.ceil(outerUnitSpan / innerUnitSpan);
  const gridWidthRaw =
    Math.ceil(
      Math.min(
        Math.max(trueEnd, Math.ceil(minUnits / innerUnitSpan)),
        Math.floor(maxUnits / innerUnitSpan)
      ) / outerUnitMultiplier
    ) * outerUnitMultiplier;
  // Final guard: even with a finite `trueEnd`, a non-finite minUnits/maxUnits/
  // unit-span could make gridWidth NaN/negative → NaN outerTicks → the
  // `new Array(ticks)` renders throw "Invalid array length". Clamp to a safe
  // non-negative integer (degenerate data → empty grid, never a crash).
  const gridWidth =
    Number.isFinite(gridWidthRaw) && gridWidthRaw > 0 ? gridWidthRaw : 0;
  const outerTicks =
    outerUnitMultiplier > 0 && Number.isFinite(gridWidth / outerUnitMultiplier)
      ? gridWidth / outerUnitMultiplier
      : 0;

  // This used to return TimelineItem[][], which would end up being
  // in a nested map of rows.map(row.map(item => Element))
  // Due to the drag handlers we cannot do it, as the nestedness leads
  // to lose of the DOM element if a dragged element switches row
  // If we just return the positions, we can render a flat list and avoid
  // this problem.
  const [allItems, itemRows, itemOrders] = useMemo(() => {
    const _allItems = mainPlaceholder ? [...items, mainPlaceholder] : items;
    const sortedItems = sortBy(_allItems, (i) => i.start);

    if (layout === 'GROUPED') {
      const CLAMP_FACTOR = 7 * innerUnitSpan;
      const groupedByCommonStart = Object.values(
        groupBy(sortedItems, (i) => i.start)
      ).map((group) => {
        const maxEnd = Math.max(...group.map((i) => i.end));
        const maxEndClamped = Math.ceil(maxEnd / CLAMP_FACTOR) * CLAMP_FACTOR;
        const x = {
          xStart: group[0].start,
          xEnd: maxEndClamped,
          ySpan: group.length,
          items: group
        };
        return x;
      });

      const __rows: TimelineItemWithDimensions[][] = [];
      groupedByCommonStart.forEach((g) => {
        const order = getOrder2(__rows, g);
        g.items.forEach((item, i) => {
          const row = __rows[order + i] || [];
          row.push({
            item,
            xEnd: g.xEnd,
            xStart: g.xStart
          });
          __rows[order + i] = row;
        });
      });

      const _orders = __rows.reduce<{ [key: string]: number }>((m, rs, i) => {
        rs.forEach((r) => (m[r.item.key] = i));
        return m;
      }, {});
      return [sortedItems, __rows, _orders] as const;
    } else {
      const _rows: TimelineItem[][] = [];

      // pin a dragged item to it's line, by reassigning its order before the layout is calculated
      if (mainDrag && mainDrag.currentOrder !== null) {
        times(mainDrag.currentOrder).forEach((o) => (_rows[o] = []));
        const updatedDraggedItem = _allItems.find(
          (x) => x.key === mainDrag.item.key
        );
        if (updatedDraggedItem) {
          _rows[mainDrag.currentOrder] = [updatedDraggedItem];
        }
      }

      const _orders_temp: { [key: string]: number } = {};

      const naivelySorted = orderBy(_allItems, [
        (i) => i.start,
        // this is so that connected items are evaluated first. This is useful because they need to stay close to their cause - we don't want them to get pushed down by other items
        (i) => (i.parents?.length ? 0 : 1)
      ]);
      const naiveSortKeys = Object.fromEntries(
        naivelySorted.map((x, i) => [x.key, i])
      );
      const optimizedSortKeys = Object.fromEntries(
        naivelySorted.map((x) => {
          const sortKeys = [naiveSortKeys[x.key]];
          const parentKeys = x.parents?.map((p) => naiveSortKeys[p]) || [];
          if (parentKeys.length) {
            sortKeys.unshift(Math.max(...parentKeys));
          }

          return [
            x.key,
            sortKeys.map((s) => padStart(s.toString(), 6, '0')).join('')
          ];
        })
      );

      const itemsInBestProcessingOrder = sortBy(
        _allItems,
        (x) => optimizedSortKeys[x.key]
      );

      const USE_NAIVELY_SORTED = true;

      (USE_NAIVELY_SORTED ? naivelySorted : itemsInBestProcessingOrder).forEach(
        (d) => {
          if (d.key === mainDrag?.item.key) {
            return;
          }
          const minOrders = compact(
            (d.parents || []).map((x) => _orders_temp[x])
          );
          const minOrder = minOrders.length ? Math.min(...minOrders) : null;
          const order = getOrder(_rows, d, minOrder);
          const row = _rows[order] || [];
          row.push(d);
          _rows[order] = row;
          _orders_temp[d.key] = order;
        }
      );
      const _orders = _rows.reduce<{ [key: string]: number }>((m, rs, i) => {
        rs.forEach((r) => (m[r.key] = i));
        return m;
      }, {});
      // This is so weird and I have no explanation for it, but:
      // _orders_temp and _orders are identical besides the order in which
      // the keys are in these objects.
      // If you pass _orders_temp to the outside, moving of items breaks
      // completely. If you pass _orders, it works.
      // I don't understand why - but here we are.

      return [_allItems, _rows, _orders] as const;
    }
  }, [items, mainPlaceholder, mainDrag, innerUnitSpan, layout]);

  const annotationRows = annotations.length || alwaysDrawAnnotationLane ? 1 : 0;
  const coordRows = grid ? 1 : 0;
  const rulerRows = rulers?.length ? 1 : 0;
  const headerRows = header ? 1 : 0;
  const secondaryHeaderRowsLaneIndices = sortBy(
    uniq((secondaryHeaderElements || []).map((s) => s.lane)),
    (x) => x
  );

  const secondaryHeaderRows = secondaryHeaderRowsLaneIndices.length;

  const gridRows = Math.max(itemRows.length, minRows);
  const totalRows =
    gridRows + coordRows + headerRows + secondaryHeaderRows + annotationRows;
  const mainDragHandlers = useRelativeDrag();
  const secondaryDragHandlers = useRelativeDrag();
  const getUnitWidth = useCallback(
    (gridEl: HTMLDivElement) =>
      Math.max(minColWidth, gridEl.getBoundingClientRect().width / gridWidth),
    [minColWidth, gridWidth]
  );

  useEffect(() => {
    const grid = gridRef.current;
    if (grid && onScaleChangeRequest && ENABLE_SCROLL_WHEEL_ZOOM) {
      const handler = (ev: WheelEvent) => {
        ev.preventDefault();
        onScaleChangeRequest(ev.deltaY);
      };
      grid.addEventListener('wheel', handler);
      return () => grid.removeEventListener('wheel', handler);
    }
  }, [onScaleChangeRequest]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !scrollIntoView) {
      return;
    }

    const unitWidth = getUnitWidth(grid);
    const focusPosition = unitWidth * (scrollIntoView / innerUnitSpan);
    const isVisible =
      focusPosition > grid.scrollLeft &&
      focusPosition < grid.scrollLeft + grid.clientWidth;
    if (isVisible) {
      return;
    }
    const nextX = focusPosition - grid.clientWidth / 2;
    grid.scrollTo({ left: nextX });

    // we only want to react to changes to scrollIntoView, the other changes are not relevant for us
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollIntoView]);

  // This was originally supposed to be triggered by the Background of the secondary header lane
  // Elements within this lane, that do not have a fixed end, stretch across the whole lane.
  // They'll therefore block the click event. To work around this, the inner elements also
  // need to receive this handler
  const secondaryHeaderElementCreateClick = useCallback(
    (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      console.log(ev.target, secondaryDragRef.current);
      if (createSecondaryHeaderElement && secondaryDragRef.current) {
        const unitWidth = getUnitWidth(secondaryDragRef.current);
        const outerEl = secondaryDragRef.current;
        const rect = outerEl.getBoundingClientRect();
        const origX = ev.clientX - rect.left + outerEl.scrollLeft;
        const start = Math.floor(origX / unitWidth) * innerUnitSpan;
        const end =
          createSecondaryHeaderElement?.defaultDuration === null
            ? null
            : start +
              (createSecondaryHeaderElement.defaultDuration ?? innerUnitSpan);

        console.log(start, end);
        createSecondaryHeaderElement.onCreate({ start, end }, 'click');
      }
    },
    [innerUnitSpan, createSecondaryHeaderElement, getUnitWidth]
  );

  const mainDragStartRow = headerRows + rulerRows + secondaryHeaderRows + 1;

  return (
    <GridContainer>
      <Grid
        columns={gridWidth}
        rows={totalRows}
        gridRows={gridRows}
        rulerRows={rulerRows}
        headerRows={headerRows}
        secondaryHeaderRows={secondaryHeaderRows}
        minColWidth={minColWidth}
        minRowHeight={minRowHeight}
        ref={gridRef}
      >
        {grid && (
          <Coordinates
            span={outerUnitMultiplier}
            ticks={outerTicks}
            rows={totalRows}
            offset={rulerRows}
          />
        )}
        {header && (
          <Headers
            outerTicks={outerTicks}
            outerUnitMultiplier={outerUnitMultiplier}
            renderOuterUnit={(i) => renderOuterUnit(i, gridOffset)}
            offset={rulerRows}
          />
        )}
        {annotations.map((a) => (
          <Annotation
            key={a.key}
            d={a}
            row={gridRows + headerRows + secondaryHeaderRows + rulerRows + 1}
            innerUnitSpan={innerUnitSpan}
            minColWidth={minColWidth}
            gridWidth={gridWidth}
            gridOffset={gridOffset}
          />
        ))}
        {secondaryHeaderElements && (
          <>
            <SecondaryHeaderElementsBackground
              row={headerRows + rulerRows}
              rows={secondaryHeaderRows}
              cols={gridWidth}
              ref={secondaryDragRef}
              onClick={(ev) => {
                if (ev.target === secondaryDragRef.current) {
                  secondaryHeaderElementCreateClick(ev);
                }
              }}
            />
            {secondaryHeaderElements.map((e) => (
              <SecondaryHeaderElement
                key={e.key}
                e={e}
                innerUnitSpan={innerUnitSpan}
                gridWidth={gridWidth}
                minColWidth={minColWidth}
                row={
                  headerRows +
                  rulerRows +
                  secondaryHeaderRowsLaneIndices.indexOf(e.lane)
                }
                dragRef={secondaryDragRef}
                dragHandlers={secondaryDragHandlers}
                drag={secondaryDrag}
                setDrag={setSecondaryDrag}
                onMove={onSecondaryHeaderMove}
                onOuterClick={secondaryHeaderElementCreateClick}
                isFirstLane={
                  secondaryHeaderRowsLaneIndices.indexOf(e.lane) === 0
                }
                isLastLane={
                  secondaryHeaderRowsLaneIndices.indexOf(e.lane) ===
                  secondaryHeaderRowsLaneIndices.length - 1
                }
                gridOffset={gridOffset}
              />
            ))}
          </>
        )}
        {rulers && (
          <Rulers
            startRow={0}
            rows={totalRows + rulerRows}
            ds={rulers}
            innerUnitSpan={innerUnitSpan}
            gridOffset={gridOffset}
          />
        )}
        {emptyOverlay && !allItems.length && (
          <OverlayContainer
            startRow={mainDragStartRow}
            rows={gridRows}
            startColumn={1}
            columns={gridWidth}
          >
            {emptyOverlay}
          </OverlayContainer>
        )}
        <MainDragContainer
          startRow={mainDragStartRow}
          rows={gridRows + coordRows}
          columns={gridWidth}
          ref={mainDragRef}
          draggable={true}
          onDragStart={(ev) => {
            mainDragHandlers.onDragStart(ev);
            hideDragPreview(ev);
          }}
          onDrag={(ev) => {
            console.log('DRAG', ev);
            mainDragHandlers.onDrag(ev, (nextPos, origPos) => {
              if (
                (mainDrag === null || mainPlaceholder) &&
                create &&
                mainDragRef.current
              ) {
                const unitWidth = getUnitWidth(mainDragRef.current);

                const outerEl = mainDragRef.current;
                const rect = outerEl.getBoundingClientRect();
                const origX = origPos.x - rect.left + outerEl.scrollLeft;

                // Take the vector of movement into account to determine
                // to which side to round to
                const anchor =
                  nextPos.x < 0
                    ? Math.ceil(origX / unitWidth)
                    : Math.floor(origX / unitWidth);

                const steps =
                  nextPos.x === 0
                    ? 1
                    : nextPos.x < 0
                    ? Math.floor(nextPos.x / unitWidth)
                    : Math.ceil(nextPos.x / unitWidth);

                const start = Math.max(steps < 0 ? anchor + steps : anchor, 0);
                const end = Math.max(steps < 0 ? anchor : anchor + steps, 1);

                setMainPlaceholder({
                  key: '___PLACEHOLDER___',
                  start: start * innerUnitSpan,
                  end: end * innerUnitSpan,
                  body: create.renderPlaceholder()
                });
              }
            });
          }}
          onDragEnd={(ev) => {
            if (mainPlaceholder && create) {
              create.onCreate(
                { start: mainPlaceholder.start, end: mainPlaceholder.end },
                'drag'
              );
            }
            mainDragHandlers.onDragEnd(ev);
            setMainPlaceholder(null);
          }}
          onClick={(ev) => {
            if (create && ev.target === mainDragRef.current) {
              const unitWidth = getUnitWidth(mainDragRef.current);
              const outerEl = mainDragRef.current;
              const rect = outerEl.getBoundingClientRect();
              const origX = ev.clientX - rect.left + outerEl.scrollLeft;
              const start = Math.floor(origX / unitWidth) * innerUnitSpan;
              const end = start + (create.defaultDuration ?? innerUnitSpan);
              create.onCreate({ start, end }, 'click');
            }
          }}
        />
        {allItems.map((r) => (
          <Item
            key={r.key}
            r={r}
            innerUnitSpan={innerUnitSpan}
            headerRows={headerRows + rulerRows + secondaryHeaderRows}
            onMove={onMove}
            order={itemOrders[r.key]}
            dragHandlers={mainDragHandlers}
            drag={mainDrag?.item.key === r.key ? mainDrag : null}
            setDrag={setMainDrag}
            dragRef={mainDragRef}
            minColWidth={minColWidth}
            gridWidth={gridWidth}
            gridOffset={gridOffset}
          />
        ))}
        {mainDrag &&
          mainDrag.mode !== 'move-all' &&
          times(gridWidth * gridRows).map((i) => {
            // Better to render in a flat list, so that DOM
            // references can stay intact
            const col = i % gridWidth;
            const row = Math.floor(i / gridWidth);
            return (
              <div
                key={i}
                style={{
                  zIndex: 1,
                  gridRow: row + mainDragStartRow,
                  gridColumn: col + 1
                }}
                onDragEnter={(ev) => {
                  if (!onMove || !mainDrag) {
                    return;
                  }

                  console.log('ENTERING DRAG');
                  const { item } = mainDrag;
                  if (mainDrag.mode === 'move-start') {
                    let nextStart = col * innerUnitSpan;
                    let nextEnd = item.end;
                    if (nextStart === item.end) {
                      nextStart = nextStart - innerUnitSpan;
                    }
                    if (nextStart > item.end) {
                      nextEnd = nextStart;
                      nextStart = item.end;
                    }
                    onMove(
                      item.key,
                      {
                        next: {
                          start: nextStart,
                          end: nextEnd
                        },
                        prev: { start: item.start, end: item.end },
                        diff: {
                          start: nextStart - item.start,
                          end: nextEnd - item.end
                        }
                      },
                      item.data
                    );
                  }
                  if (mainDrag.mode === 'move-end') {
                    let nextStart = item.start;
                    let nextEnd = col * innerUnitSpan;
                    if (nextEnd === item.start) {
                      nextEnd = nextEnd + innerUnitSpan;
                    }
                    if (nextEnd < item.start) {
                      nextStart = nextEnd;
                      nextEnd = item.start;
                    }
                    onMove(
                      item.key,
                      {
                        next: {
                          start: nextStart,
                          end: nextEnd
                        },
                        prev: { start: item.start, end: item.end },
                        diff: {
                          start: nextStart - item.start,
                          end: nextEnd - item.end
                        }
                      },
                      item.data
                    );
                  }
                }}
              />
            );
          })}
      </Grid>
    </GridContainer>
  );
};

const StandardColoredItemContainer = styled('div')<{
  color: string;
  fontColor?: string;
  borderLeftColor?: string;
  fitContent?: boolean;
  margin?: 'dense' | 'normal';
}>((p) => {
  const radius = p.theme.custom.border.radius();
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: p.color,
    color: p.fontColor || 'inherit',
    borderRadius: radius,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontFamily: p.theme.typography.fontFamily,
    fontSize: p.theme.custom.fontSize.s,
    lineHeight: p.margin === 'dense' ? 1.3 : 1.75,
    width: p.fitContent ? 'fit-content' : 'auto',
    borderLeft: p.borderLeftColor
      ? `${radius * 8}px solid ${p.borderLeftColor}`
      : 'none'
  };
});

const ConnectorGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'min-content 1fr min-content',
  alignItems: 'center'
  // marginLeft: p.theme.spacing(1)
}));

const ConnectorArmContainer = styled('div')((p) => ({
  position: 'relative',
  width: p.theme.spacing(0.5),
  height: '100%',
  marginLeft: -p.theme.spacing(0.5)
}));

const ConnectorArmHorizontal = styled('div')((p) => ({
  position: 'absolute',
  height: '2px',
  backgroundColor: p.theme.custom.colors.gray.w400,
  top: '50%',
  left: 0,
  // width: '100%',

  marginLeft: -1.5,
  width: 5.5
}));

const ConnectorArmVertical = styled('div')<{
  shape: 'L' | 'T';
}>((p) => ({
  position: 'absolute',
  borderLeft: `2px solid ${p.theme.custom.colors.gray.w400}`,
  borderSize: 2,
  top: -p.theme.spacing(0.5),
  left: 0,
  height: `calc(${p.shape === 'L' ? '50%' : '100%'} + ${p.theme.spacing(
    0.5
  )}px)`,
  width: '100%',
  marginLeft: -p.theme.spacing(1)
}));

const ConnectorArmVertical2 = styled('div')<{
  shape: 'up' | 'down' | 'upAndDown';
}>((p) => ({
  position: 'absolute',
  top: p.shape === 'down' ? '50%' : -p.theme.spacing(0.5),
  left: 0,
  height: `calc(${
    p.shape === 'up' || p.shape === 'down' ? '50%' : '100%'
  } + ${p.theme.spacing(p.shape === 'upAndDown' ? 1 : 0.5)}px)`,
  marginLeft: -1.5,
  width: '2px',
  backgroundColor: p.theme.custom.colors.gray.w400
}));

const ConnectorArmLeft2 = ({
  shape
}: {
  shape: 'up' | 'down' | 'upAndDown';
}) => {
  return (
    <ConnectorArmContainer>
      <ConnectorArmVertical2 shape={shape} />
      <ConnectorArmHorizontal />
    </ConnectorArmContainer>
  );
};

const ConnectorArmLeft = ({ shape }: { shape: 'L' | 'T' }) => {
  return (
    <ConnectorArmContainer>
      <ConnectorArmVertical shape={shape} />
      <ConnectorArmHorizontal />
    </ConnectorArmContainer>
  );
};

const ConnectorArmRight = styled('div')((p) => ({
  height: '2px',
  backgroundColor: p.theme.custom.colors.gray.w400,
  width: p.theme.spacing(1),
  marginRight: -p.theme.spacing(1),
  marginTop: 2
}));

const StandardColoredItem = React.forwardRef<
  any,
  PropsWithChildren<{
    color: string;
    fontColor?: string;
    borderLeftColor?: string;
    fitContent?: boolean;
    margin?: 'dense' | 'normal';
    style?: React.CSSProperties;
    connector?: 'L' | 'T';
  }>
>(({ connector, ...props }, ref) => {
  if (connector) {
    return (
      <ConnectorGrid>
        <ConnectorArmLeft shape={connector} />
        <StandardColoredItemContainer {...props} ref={ref} />
      </ConnectorGrid>
    );
  }
  return <StandardColoredItemContainer {...props} ref={ref} />;
});

const ItemSimple = styled('div')`
  && {
    width: 100%;
    justify-content: flex-start;
    padding: ${(p) => `${p.theme.spacing()}px ${p.theme.spacing()}px`};
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    font-weight: 200;
  }
`;

const ItemButton = ItemSimple.withComponent(ButtonBase);

export const ColoredItem = React.forwardRef<
  any,
  PropsWithChildren<{
    color: string;
    fontColor?: string;
    borderLeftColor?: string;
    style?: React.CSSProperties;
    fitContent?: boolean;
    margin?: 'dense' | 'normal';
    connector?: 'L' | 'T';
  }>
>(
  (
    {
      color,
      fontColor,
      borderLeftColor,
      children,
      style,
      fitContent,
      margin,
      connector
    },
    ref
  ) => {
    return (
      <StandardColoredItem
        ref={ref}
        color={color}
        fontColor={fontColor}
        borderLeftColor={borderLeftColor}
        style={style}
        fitContent={fitContent}
        margin={margin}
        connector={connector}
      >
        <ItemSimple>{children}</ItemSimple>
      </StandardColoredItem>
    );
  }
);

export const ColoredItemButton = React.forwardRef<
  any,
  PropsWithChildren<{
    color: string;
    onClick: () => void;
    fontColor?: string;
    borderLeftColor?: string;
    style?: React.CSSProperties;
    fitContent?: boolean;
    connector?: 'L' | 'T';
    margin?: 'dense' | 'normal';
  }>
>(
  (
    {
      color,
      onClick,
      children,
      fontColor,
      borderLeftColor,
      style,
      fitContent,
      connector,
      margin
    },
    ref
  ) => {
    return (
      <StandardColoredItem
        color={color}
        ref={ref}
        fontColor={fontColor}
        borderLeftColor={borderLeftColor}
        style={style}
        fitContent={fitContent}
        margin={margin}
        connector={connector}
      >
        <ItemButton onClick={onClick}>{children}</ItemButton>
      </StandardColoredItem>
    );
  }
);

const SliderContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  color: p.theme.palette.background.paper,
  background: 'rgba(0,0,0,.7)',
  borderRadius: p.theme.custom.border.radius(4),
  zIndex: 1,
  padding: `0 ${p.theme.spacing(0.5)}px`,

  gap: p.theme.spacing(0.5),

  '@media (pointer: coarse)': {
    borderRadius: p.theme.spacing(3),
    paddingRight: p.theme.spacing(1.5),
    paddingLeft: p.theme.spacing(1.5)
  }
}));

export const ZoomControls = React.memo(
  ({
    value,
    onChange,
    min,
    max
  }: {
    value: number;
    onChange: (nextValue: number) => void;
    min: number;
    max: number;
  }) => {
    const scale = (x: number) => x;
    const step = Math.round(max - min) / 40;
    const theme = useTheme();
    return (
      <SliderContainer>
        <IconButton
          onClick={() => onChange(scale(value - step))}
          color="inherit"
        >
          <Minus size={16} />
        </IconButton>
        <Slider
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(ev, newValue) => onChange(newValue as number)}
          scale={scale}
          classes={{
            root: css({
              color: `${theme.palette.background.paper} !important`
            })
          }}
        />
        <IconButton
          onClick={() => onChange(scale(value + step))}
          color="inherit"
        >
          <Plus size={16} />
        </IconButton>
      </SliderContainer>
    );
  }
);

export const useScaleChangeFn = (
  onChange: React.Dispatch<React.SetStateAction<number>>,
  min: number,
  max: number
) => {
  return useCallback(
    (deltaY: number) => {
      onChange((prev) => {
        const step = Math.round(max - min) / 40;
        const accelerator = Math.abs(deltaY) > 10 ? 2 : 1;
        const increment = step * accelerator * (deltaY > 0 ? 1 : -1);
        const nextValue = prev + increment;
        return Math.max(min, Math.min(max, nextValue));
      });
    },
    [onChange, min, max]
  );
};

const TimelineItem2Container = styled('div')<{
  fontColor?: string;
  bgColor?: string;
  variant?: 'dense' | 'normal' | 'placeholder';
  transparent?: boolean;
  hasMenu?: boolean;
}>((p) => ({
  display: 'grid',
  gridTemplateColumns: `${
    p.variant === 'dense' ? p.theme.spacing(1) : p.theme.spacing(4)
  }px 1fr`,
  alignItems: 'center',

  borderRadius: p.theme.custom.border.radius(2 / 3),

  color: p.fontColor || 'inherit',
  backgroundColor:
    p.variant === 'placeholder'
      ? p.theme.custom.colors.blue.w400
      : p.bgColor || p.theme.custom.colors.gray.w300,
  opacity: p.transparent ? 0.4 : p.variant === 'placeholder' ? 0.5 : 1,
  cursor: p.onClick ? 'pointer' : 'default',
  ...(p.hasMenu
    ? {
        ':hover': {
          backgroundColor: p.theme.custom.colors.gray.w400
        }
      }
    : {})
}));

const OVERLAP = 4;

const TimelineItem2Left = styled('div')<{
  bgColor: string;
  connectUp: boolean;
  connectDown: boolean;
}>((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: p.bgColor,
  color: p.theme.custom.colors.gray.white,
  height:
    p.connectUp || p.connectDown
      ? `calc(100% + ${OVERLAP * (p.connectUp && p.connectDown ? 2 : 1)}px)`
      : '100%',
  marginTop: p.connectUp ? -OVERLAP : 0,
  marginBottom: p.connectDown ? -OVERLAP : 0,
  paddingTop: p.connectUp ? OVERLAP : 0,
  paddingBottom: p.connectDown ? OVERLAP : 0,
  borderTopLeftRadius: p.connectUp ? 0 : p.theme.custom.border.radius(0.75),
  borderBottomLeftRadius: p.connectDown ? 0 : p.theme.custom.border.radius(0.75)
}));

const TimelineItem2ContainerContent = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: p.theme.spacing(),
  margin: p.theme.spacing(),
  overflow: 'hidden',
  opacity: 1
}));

const TimelineItem2Title = styled('div')((p) => ({
  whiteSpace: 'nowrap'
}));

const TimelineItem2Right = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500,
  fontWeight: 'normal',
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  maxHeight: 16
}));

export const TimelineItemTitleWithSuperTile = ({
  title,
  superTitle
}: {
  title: React.ReactNode;
  superTitle: React.ReactNode;
}) => {
  return (
    <FlexContainer direction="column" alignItems="flex-start" spacing={0}>
      {superTitle && (
        <Typography variant="caption">
          <strong>{superTitle}</strong>
        </Typography>
      )}
      <div>{title}</div>
    </FlexContainer>
  );
};

export const TimelineItem2 = React.forwardRef<
  HTMLDivElement,
  {
    leftColor?: string;
    leftIcon?: Icon;
    variant?: 'dense' | 'normal' | 'placeholder';
    right?: React.ReactNode;
    title?: React.ReactNode;

    bgColor?: string;
    fontColor?: string;
    transparent?: boolean;

    connector?: 'L' | 'T';
    onClick?: () => void;
    style?: React.CSSProperties;
    selected?: boolean;
    onSelect?: (nextSelected: boolean) => void;
    selectMenu?: React.ReactNode;

    connectUp?: boolean;
    connectDown?: boolean;
    connectRight?: boolean;
    connectLeft?: 'up' | 'down' | 'upAndDown';
  }
>(
  (
    {
      leftColor,
      leftIcon,
      variant = 'normal',
      right,
      title,
      fontColor,
      bgColor,
      transparent,
      connector,
      onClick,
      style,
      selected,
      onSelect,
      selectMenu,

      connectUp,
      connectDown,
      connectLeft,
      connectRight
    },
    ref
  ) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const menuHovered = useHover(menuRef);
    const iconRef = useRef<HTMLDivElement>(null);
    const iconHovered = useHover(iconRef);
    const hoverActive = iconHovered && !!onSelect;
    const body = (
      <TimelineItem2Container
        ref={ref}
        role={onClick ? 'button' : undefined}
        onClick={onClick}
        fontColor={fontColor}
        bgColor={bgColor}
        style={style}
        variant={variant}
        transparent={transparent}
        hasMenu={!!selectMenu}
      >
        {variant !== 'placeholder' && (
          <TimelineItem2Left
            bgColor={leftColor || 'inherit'}
            ref={iconRef}
            connectUp={!!connectUp}
            connectDown={!!connectDown}
          >
            {variant === 'normal' && (
              <>
                {leftIcon &&
                  !selected &&
                  !hoverActive &&
                  React.createElement(leftIcon, { size: 16 })}
                {selected && (
                  <Checkbox
                    checked={true}
                    size="small"
                    onClick={(ev) => {
                      if (onSelect) {
                        ev.stopPropagation();
                        onSelect(false);
                      }
                    }}
                  />
                )}
                {!selected && hoverActive && (
                  <Checkbox
                    checked={false}
                    size="small"
                    onClick={(ev) => {
                      if (onSelect) {
                        ev.stopPropagation();
                        onSelect(true);
                      }
                    }}
                  />
                )}
              </>
            )}
          </TimelineItem2Left>
        )}
        <TimelineItem2ContainerContent ref={menuRef}>
          {variant !== 'dense' && (
            <TimelineItem2Title>{title}</TimelineItem2Title>
          )}
          {variant === 'normal' && (
            <TimelineItem2Right>
              {/* Explicitly keep these in the DOM. When selectMenu shows, a mouseleave event is triggered, which removes the selectMenu from the DOM again */}
              {selectMenu ? (
                <>
                  <div hidden={!menuHovered}>{selectMenu}</div>
                  <div hidden={menuHovered}>{right}</div>
                </>
              ) : (
                right
              )}
            </TimelineItem2Right>
          )}
        </TimelineItem2ContainerContent>
      </TimelineItem2Container>
    );

    if (!connectRight && !connectLeft) {
      return body;
    }

    return (
      <ConnectorGrid>
        {connectLeft ? <ConnectorArmLeft2 shape={connectLeft} /> : <div />}
        {body}
        {connectRight ? <ConnectorArmRight /> : <div />}
      </ConnectorGrid>
    );
  }
);
